import { useFeature } from "@optimizely/react-sdk";

import {
  EBackendSubscriptionStatus,
  ESubscriptionStatus,
  EUnitDetailsFilter,
  IBrilliantDevices,
  IHomeSubscription,
  IIntegrationsCard,
} from "../../interfaces/interfaces";
import InstallationSettingsCard from "../unitDetails/InstallationSettingsCard";
import {
  BrilliantDevices,
  IntegrationDevices,
} from "../unitDetails/common/CommonUnitDetailsComponents";
import {
  isDateMoreThanThirtyMinutesOld,
  mapSubscriptionStatus,
} from "../../utils/utils";

import ViewMembers from "./ViewMembers";

export interface HomeSmallBodyProps {
  brilliantDevices: IBrilliantDevices;
  currentUnitDetailsFilter: EUnitDetailsFilter;
  enableBrilliantMaxSubscription?: boolean;
  propertyId: string;
  integrations: IIntegrationsCard[];
  openCancelBrilliantMaxModal: () => void;
  openEnrollBrilliantMaxModal: () => void;
  reactivateBrilliantMax: () => void;
  setShowInviteTenantModal: React.Dispatch<React.SetStateAction<boolean>>;
  openEditTenantModalWizard: () => void;
  setShowHomePasscodeModal: React.Dispatch<React.SetStateAction<boolean>>;
  subscription?: IHomeSubscription | null;
}

const renderSubscriptionButton = (
  status: ESubscriptionStatus,
  isCanceling: boolean,
  openCancelBrilliantMaxModal: () => void,
  openEnrollBrilliantMaxModal: () => void,
  reactivateBrilliantMax: () => void
): JSX.Element => {
  if (status === ESubscriptionStatus.NotSubscribed) {
    return (
      <button
        className="tertiary-btn-small"
        type="button"
        onClick={openEnrollBrilliantMaxModal}
      >
        Enroll in Brilliant Max
      </button>
    );
  }
  if (status === ESubscriptionStatus.SelfManaged) {
    return (
      <button
        className="tertiary-btn-small"
        disabled
        type="button"
        onClick={() => {}}
      >
        Enrolled in Brilliant Max
      </button>
    );
  }
  // Pro-managed subscription that is scheduled to cancel: offer reactivation
  // instead of cancellation. Clicking opens the Stripe portal in a new tab.
  if (isCanceling) {
    return (
      <button
        className="tertiary-btn-small"
        type="button"
        onClick={reactivateBrilliantMax}
      >
        Reactivate Brilliant Max
      </button>
    );
  }
  return (
    <button
      className="tertiary-btn-small tertiary-btn-small-alert"
      type="button"
      onClick={openCancelBrilliantMaxModal}
    >
      Cancel Brilliant Max
    </button>
  );
};

const HomeSmallBody: React.FC<HomeSmallBodyProps> = ({
  brilliantDevices,
  currentUnitDetailsFilter,
  enableBrilliantMaxSubscription = false,
  propertyId,
  integrations,
  openCancelBrilliantMaxModal,
  openEnrollBrilliantMaxModal,
  reactivateBrilliantMax,
  setShowInviteTenantModal,
  openEditTenantModalWizard,
  setShowHomePasscodeModal,
  subscription,
}) => {
  const [isFeatureEnabled] = useFeature("brilliant_max_subscription");
  const isBrilliantMaxEnabled =
    isFeatureEnabled || enableBrilliantMaxSubscription;
  const subscriptionStatus = mapSubscriptionStatus(subscription);
  // A Pro-managed subscription that is scheduled to cancel ends on its current
  // period end instead of renewing, and offers reactivation.
  const isCanceling =
    subscription?.status === EBackendSubscriptionStatus.Canceling;
  const subscriptionButton =
    isBrilliantMaxEnabled && subscriptionStatus
      ? renderSubscriptionButton(
          subscriptionStatus,
          isCanceling,
          openCancelBrilliantMaxModal,
          openEnrollBrilliantMaxModal,
          reactivateBrilliantMax
        )
      : null;
  const IsEveryControlOffline =
    brilliantDevices.installedBrilliantControls.every((control) =>
      isDateMoreThanThirtyMinutesOld(control.lastKnownCloudSync)
    );
  const showSmallContainerForResidentViewWithNoMargin =
    currentUnitDetailsFilter === EUnitDetailsFilter.residents;
  return (
    <div
      className={`brilliant-container-small ${
        showSmallContainerForResidentViewWithNoMargin
          ? "brilliant-container-small-no-margins"
          : ""
      }`}
      data-testid="brilliant-devices-container"
    >
      {currentUnitDetailsFilter === EUnitDetailsFilter.brilliants && (
        <>
          <BrilliantDevices
            brilliantControls={brilliantDevices.installedBrilliantControls}
            brilliantSwitches={brilliantDevices.installedBrilliantSwitches}
            brilliantPlugs={brilliantDevices.installedBrilliantPlugs}
            isInReconfigState={false}
            isProView
          />

          <div className="brilliant-container-name menu brilliant-container-small-padding">
            Installation Settings
          </div>
          <div className="brilliant-container-cards">
            <div data-testid="reset-installation-card">
              <InstallationSettingsCard
                data={{ settingName: "Home Passcode" }}
                openModalFn={() => {
                  setShowHomePasscodeModal(true);
                }}
              />
            </div>
          </div>
        </>
      )}

      {currentUnitDetailsFilter === EUnitDetailsFilter.integrations && (
        <div data-testid="integration-devices">
          <IntegrationDevices
            integrationDevices={integrations}
            isInReconfigState={false}
            IsEveryControlOffline={IsEveryControlOffline}
          />
        </div>
      )}

      {currentUnitDetailsFilter === EUnitDetailsFilter.residents && (
        <div className="brilliant-container-residents">
          <ViewMembers
            propertyId={propertyId}
            openEditResidentModal={openEditTenantModalWizard}
            setShowInviteTenantModal={setShowInviteTenantModal}
            subscriptionButton={subscriptionButton}
          />
        </div>
      )}
    </div>
  );
};

export default HomeSmallBody;
